import { DOCUMENT, Inject, Injectable } from '@angular/core';
import { EventManager } from '@angular/platform-browser';
import { registerWithGlobalApp } from '@app/utils/app';
import { loggerFor } from '@app/utils/logger';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';

type Options = {
  /**
   * The DOM element the listener will be added to.
   * Default: body
   */
  element?: HTMLElement;

  /**
   * Optional function to be called before the handler. Skips the handler if it returns true.
   * @param event Keyboard event that triggered the callback.
   */
  ignore?: (event: KeyboardEvent) => boolean;

  /**
   * String that specifies which keys to listen for.
   * Format: `{optional modifiers}.key`
   * Examples:
   *  - control.k
   *  - control.alt.u
   *  - escape
   */
  keys: PossibleShortcut;

  /**
   * Calls event.preventDefault() if true.
   */
  prevent?: boolean;

  /**
   * Calls event.stopPropagation() if true.
   */
  stop?: boolean;

  /**
   * Calls event.stopImmediatePropagation() if true.
   */
  stopImmediate?: boolean;
};

@Injectable({ providedIn: 'root' })
export class HotkeyService {
  private defaults: Partial<Options> = {
    element: this.document.body,
    prevent: false,
    stop: false,
    stopImmediate: false,
  };

  private disabled: boolean = false;

  /**
   * Map used to detect multiple shortcuts with the same combination.
   */
  private eventMap: Record<string, HTMLElement[]> = {};
  private logger = loggerFor('HotkeyService');

  get events() {
    return this.eventMap;
  }

  constructor(
    private eventManager: EventManager,
    @Inject(DOCUMENT) private document: Document
  ) {
    if (environment.production === false) {
      registerWithGlobalApp('hotkeys', this);
    }
  }

  /**
   * Create a keyboard shortcut and attach it to a dom element (default: body).
   */
  addShortcut(options: Partial<Options>): Observable<KeyboardEvent> {
    const mergedOptions = { ...this.defaults, ...options };
    const event = `keydown.${mergedOptions.keys}`;

    /*
     * TODO Improve detection of duplicate shortcuts (currently it only detects
     *  duplicate shortcuts if the modifiers are defined in the same order).
     */
    if (this.eventMap[event]?.length > 0) {
      this.logger.warn(`There are existing event handlers for this shortcut. shortcut=${mergedOptions.keys}`);
    }

    return new Observable((observer) => {
      this.remember(event, mergedOptions.element);

      const handler = (e: KeyboardEvent) => {
        if (this.disabled || (options.ignore != null && options.ignore(e))) {
          return;
        }

        if (options.prevent) {
          e.preventDefault();
        }

        if (options.stop) {
          e.stopPropagation();
        }

        if (options.stopImmediate) {
          e.stopImmediatePropagation();
        }

        observer.next(e);
      };

      const dispose = this.eventManager.addEventListener(mergedOptions.element, event, handler);

      return () => {
        dispose();

        this.forget(event, mergedOptions.element);
      };
    });
  }

  /**
   * Disable all shortcuts.
   */
  disable(): void {
    this.disabled = true;
  }

  /**
   * Enable all shortcuts.
   */
  enable(): void {
    this.disabled = false;
  }

  private forget(event: string, element: HTMLElement): void {
    this.eventMap[event] = this.eventMap[event].filter((el) => el !== element);
  }

  private remember(event: string, element: HTMLElement): void {
    if (this.eventMap[event] == null) {
      this.eventMap[event] = [];
    }

    this.eventMap[event].push(element);
  }
}

type Modifiers =
  | 'control'
  | 'control.alt'
  | 'control.alt.shift'
  | 'control.alt.shift.meta'
  | 'control.alt.meta'
  | 'control.alt.meta.shift'
  | 'control.shift'
  | 'control.shift.alt'
  | 'control.shift.alt.meta'
  | 'control.shift.meta'
  | 'control.shift.meta.alt'
  | 'control.meta'
  | 'control.meta.alt'
  | 'control.meta.alt.shift'
  | 'control.meta.shift'
  | 'control.meta.shift.alt'
  | 'alt'
  | 'alt.control'
  | 'alt.control.shift'
  | 'alt.control.shift.meta'
  | 'alt.control.meta'
  | 'alt.control.meta.shift'
  | 'alt.shift'
  | 'alt.shift.control'
  | 'alt.shift.control.meta'
  | 'alt.shift.meta'
  | 'alt.shift.meta.control'
  | 'alt.meta'
  | 'alt.meta.control'
  | 'alt.meta.control.shift'
  | 'alt.meta.shift'
  | 'alt.meta.shift.control'
  | 'shift'
  | 'shift.control'
  | 'shift.control.alt'
  | 'shift.control.alt.meta'
  | 'shift.control.meta'
  | 'shift.control.meta.alt'
  | 'shift.alt'
  | 'shift.alt.control'
  | 'shift.alt.control.meta'
  | 'shift.alt.meta'
  | 'shift.alt.meta.control'
  | 'shift.meta'
  | 'shift.meta.control'
  | 'shift.meta.control.alt'
  | 'shift.meta.alt'
  | 'shift.meta.alt.control'
  | 'meta'
  | 'meta.alt'
  | 'meta.alt.control'
  | 'meta.alt.control.shift'
  | 'meta.alt.shift'
  | 'meta.alt.shift.control'
  | 'meta.control'
  | 'meta.control.alt'
  | 'meta.control.alt.shift'
  | 'meta.control.shift'
  | 'meta.control.shift.alt'
  | 'meta.shift'
  | 'meta.shift.alt'
  | 'meta.shift.alt.control'
  | 'meta.shift.control'
  | 'meta.shift.control.alt';

// prettier-ignore
type PlainKey = 'a'|'b'|'c'|'d'|'e'|'f'|'g'|'h'|'i'|'j'|'k'|'l'|'m'|'n'|'o'|'p'|'q'|'r'|'s'|'t'|'u'|'v'|'w'|'x'|'y'|'z'|'0'|'1'|'2'|'3'|'4'|'5'|'6'|'7'|'8'|'9';
type SpecialKeys =
  | 'arrowDown'
  | 'arrowLeft'
  | 'arrowRight'
  | 'arrowUp'
  | 'escape'
  | 'enter'
  | 'pageUp'
  | 'delete'
  | 'space'
  | 'help'
  | 'home'
  | 'insert'
  | 'printScreen'
  | 'dot'
  | 'comma'
  | 'backspace'
  | 'F1'
  | 'F2'
  | 'F3'
  | 'F4'
  | 'F5'
  | 'F6'
  | 'F7'
  | 'F8'
  | 'F9'
  | 'F10'
  | 'F11'
  | 'F12';

type ImpossibleKeys = 'control.n' | 'control.t' | 'control.w' | 'control.shift.t' | 'shift.control.t';

export type PossibleShortcut =
  | PlainKey
  | SpecialKeys
  | Exclude<`${Modifiers}.${PlainKey}`, ImpossibleKeys>
  | `${Modifiers}.${SpecialKeys}`;
